import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Wallet, TrendingUp, Check, Sprout } from "lucide-react";

export default function AdminPayouts() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = () => api.get("/admin/payouts").then((r) => setData(r.data));
    useEffect(() => { load(); }, []);

    async function markPaid(producerId) {
        setBusy(producerId);
        try {
            const r = await api.post(`/admin/payouts/${producerId}/mark-paid`);
            toast.success(`${r.data.updated} payout(s) marcados como pagos`);
            load();
        } catch {
            toast.error("Erro ao marcar como pago");
        } finally {
            setBusy(null);
        }
    }

    if (!data) return <p className="text-wine-dark/60 label-overline">A carregar…</p>;

    return (
        <section className="space-y-6" data-testid="admin-payouts">
            <div>
                <span className="label-overline text-wine/60">Comissões</span>
                <h1 className="font-heading text-3xl text-wine-dark">Payouts aos produtores</h1>
                <p className="text-sm text-wine-dark/60 mt-1">
                    Comissão da plataforma: <strong className="text-wine">15%</strong> · Restante 85% transferido aos produtores.
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi label="Por transferir" value={`€${data.totals.pending.toFixed(2)}`} icon={Wallet} accent />
                <Kpi label="Pago histórico" value={`€${data.totals.paid.toFixed(2)}`} icon={Check} />
                <Kpi label="Volume total" value={`€${data.totals.total.toFixed(2)}`} icon={TrendingUp} />
                <Kpi label="Produtores c/ pendente" value={data.totals.producers_with_pending} icon={Sprout} />
            </div>

            <div className="bg-white rounded-md border border-gold-soft/30 p-5">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left label-overline text-wine-dark/60 border-b border-wine-dark/10">
                                <th className="py-3 px-3">Produtor</th><th>Região</th><th>Volume total</th><th>Pago</th><th>A transferir</th><th>Último</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((r) => (
                                <tr key={r.producer_id} className="border-t border-wine-dark/5 hover:bg-cream" data-testid={`payout-row-${r.producer_id}`}>
                                    <td className="py-3 px-3 font-heading text-base text-wine-dark">{r.producer_name}</td>
                                    <td className="text-xs text-wine-dark/70">{r.region}</td>
                                    <td className="text-wine-dark/70">€{r.total.toFixed(2)}</td>
                                    <td className="text-vineyard">€{r.paid.toFixed(2)}</td>
                                    <td className={`font-heading text-base ${r.pending > 0 ? "text-gold-deep" : "text-wine-dark/30"}`}>€{r.pending.toFixed(2)}</td>
                                    <td className="text-xs text-wine-dark/55">{r.last_at ? new Date(r.last_at).toLocaleDateString("pt-PT") : "—"}</td>
                                    <td>
                                        {r.pending > 0 && (
                                            <button
                                                onClick={() => markPaid(r.producer_id)}
                                                disabled={busy === r.producer_id}
                                                className="btn-wine text-[10px] px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50"
                                                data-testid={`mark-paid-${r.producer_id}`}
                                            >
                                                <Check className="h-3 w-3" /> Marcar pago
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {data.rows.length === 0 && (
                                <tr><td colSpan="7" className="text-center py-10 text-wine-dark/60">Ainda não há comissões registadas.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-gold-soft/10 border border-gold-soft/40 rounded-md p-4 text-xs text-wine-dark/70">
                Esta página acompanha o split 85/15 registado em base de dados. A integração real com <strong>Stripe Connect Express</strong> (transferências automáticas + KYC) está no roadmap P1.
            </div>
        </section>
    );
}

function Kpi({ label, value, icon: Icon, accent }) {
    return (
        <div className={`p-5 rounded-md border shadow-sm ${accent ? "bg-wine-dark text-cream border-wine-dark" : "bg-white border-gold-soft/30"}`}>
            <div className="flex items-start justify-between">
                <span className={`label-overline ${accent ? "text-gold-soft" : "text-wine/60"}`}>{label}</span>
                <Icon className={`h-4 w-4 ${accent ? "text-gold-soft" : "text-wine/40"}`} strokeWidth={1.5} />
            </div>
            <p className="font-heading text-3xl mt-2">{value}</p>
        </div>
    );
}
